import Image from 'next/image';
import hero from '../assets/where-donations-go/hero.jpg';
import { BlueArch, WhiteArch } from '../components/img/svgs';
import { Meta } from '../components/meta/meta';
import { GoalsSection } from './who-we-are';

// chart
import { ArcElement, Chart as ChartJS, Tooltip } from 'chart.js';
import { ChartProps, Pie } from 'react-chartjs-2';
ChartJS.register(ArcElement, Tooltip);

// limited edition
import impactReport from '../assets/where-donations-go/impact-report-2025.png';

export default function WhereDonationsGo() {
  return (
    <main className="mb-12 md:mb-0">
      <Meta title="where donations go" description="Where donations to Beds for Kids go" noDefaultFooter />
      <section className="relative h-[70vh] w-full overflow-hidden">
        <Image
          alt="Beds for Kids Warehouse"
          src={hero}
          className="absolute z-0 h-full w-full object-cover md:object-[center_15%]"
          sizes="100vw"
          priority
        />
        <div className="pointer-events-none absolute -bottom-[1px] left-0 right-0">
          <WhiteArch />
        </div>
      </section>
      <section className="text-blue-500">
        <h1 className="mx-auto my-4 max-w-3xl text-center font-operetta text-4xl md:text-6xl">where donations go</h1>
        <p className="mx-auto max-w-3xl px-4 text-center font-avenir text-xl leading-6">
          The Beds for Kids budget consists of three main categories: mattress and furniture purchases, rent and truck
          maintenance and staff supporting the mission. We have a North Carolina based mattress partner, Mattress Grove,
          who provides mattresses and box springs at cost as well as generously donates an allotment each month. We have
          an incredible team that works tirelessly to maintain the warehouse, keep trucks on the road and continue
          serving our families with dignity.
        </p>
        <div className="mt-16">
          <h4 className="md:py-auto mx-8 max-w-2xl border-y-2 border-green-500 px-8 py-4 text-center font-caliban text-5xl md:mx-auto md:border-x-2 md:border-y-0">
            We work hard to steward every dollar donated in the most effective way.
          </h4>
        </div>
      </section>
      <section className="bg-white px-4 pt-16 text-blue-500 lg:px-0" id="impact-report">
        <h2 className="text-center font-avenir text-3xl font-semibold lg:-mb-16 lg:text-5xl">2025 Impact Report</h2>
        <div className="mx-auto flex w-full max-w-5xl flex-col items-center rounded-lg shadow-lg">
          <Image alt="2025 Impact Report" src={impactReport} className="h-full" sizes="100vw" priority />
        </div>
      </section>
      <section className="mt-12 bg-white px-4 text-blue-500 lg:px-0">
        <h2 className="text-center font-avenir text-3xl font-semibold">2025 Donation Allocation</h2>
        <div className="mx-auto my-4 max-w-4xl border-b border-blue-500">
          <div className="mx-auto max-w-3xl">
            <DonationHalfPie />
          </div>
        </div>
        <div className="grid max-w-2xl items-center justify-center gap-4 pb-12 md:mx-auto md:grid-cols-2">
          {donationChartData.datasets[0].data.map((data, index) => (
            <label key={`${data}_${index}`} className="inline-flex items-center gap-2">
              <div
                className="h-5 w-5 rounded-full"
                style={{ backgroundColor: donationChartData.datasets[0].backgroundColor[index] }}
              />
              {data}% {donationChartData.labels[index]}
            </label>
          ))}
        </div>
        <BlueArch />
      </section>
      <section className="bg-blue-500 py-4 text-white md:pt-0">
        <GoalsSection />
      </section>
    </main>
  );
}

const donationChartData = {
  labels: [
    'staff supporting the mission',
    'rent',
    'mattress & furniture purchases',
    'trucks & truck maintenance',
    'other',
  ],
  datasets: [
    {
      label: 'Donation Allocation',
      data: [48, 22, 17, 7, 6],
      backgroundColor: ['#003f9a', '#006fd7', '#0093f0', '#00aefa', '#4dc9ff'],
      borderColor: ['#003f9a', '#006fd7', '#0093f0', '#00aefa', '#4dc9ff'],
      borderWidth: 1,
      rotation: -90,
      circumference: 180,
    },
  ],
};

const donationChartOptions: ChartProps<'pie'>['options'] = {
  plugins: {
    tooltip: {
      callbacks: {
        label: tt => {
          const sum = Number(tt.chart.data.datasets[0].data.reduce((a, b) => Number(a) + Number(b), 0));
          const percentage = Math.round((tt?.parsed * 100) / sum) + '%';
          return `${tt?.label}: ${percentage}`;
        },
      },
    },
  },
  aspectRatio: 2,
};

function DonationHalfPie() {
  return <Pie data={donationChartData} options={donationChartOptions} />;
}
